import { z } from "zod";
import {
  ModuleConfigSchema,
  type ModuleConfig as ModuleConfigType,
} from "@triedge/domain-config";
import {
  AcademicYearConfigSchema,
  DivisionSchema,
  FeeSchema,
  IntegrationStatusSchema,
  OrganizationProfileSchema,
  StaffUserSchema,
  TuitionRateSchema,
} from "@triedge/domain-core/value-objects";
import {
  EnrollmentPeriodStatus,
  StaffRole as StaffRoleEnum,
  StaffStatus as StaffStatusEnum,
} from "@triedge/domain-core/types";
import {
  DEFAULT_FINANCIAL_AID_REQUIREMENT_PROFILE,
  FinancialAidRequirementPolicySchema,
} from "./financialAidRequirementPolicy";

export {
  DivisionSchema,
  FeeSchema,
  IntegrationStatusSchema,
  ModuleConfigSchema,
  OrganizationProfileSchema,
  StaffUserSchema,
  TuitionRateSchema,
};

export const AcademicYearSchema = AcademicYearConfigSchema;

export const HistoricalBenchmarkSchema = z
  .object({
    yearLabel: z.string(),
    applicants: z.number(),
    accepted: z.number(),
    enrolled: z.number(),
    siblingPercentage: z.number(),
  })
  .passthrough();

export const GradeGenderSchema = z.enum(["all", "boys", "girls"]);

export const GradeSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    divisionId: z.string(),
    gender: GradeGenderSchema,
    capacity: z.number(),
  })
  .passthrough();

export const StaffRoleSchema = StaffRoleEnum;
export const StaffStatusSchema = StaffStatusEnum;

export const PermissionIdSchema = z.enum([
  "app_view",
  "app_review",
  "app_status",
  "app_notes",
  "app_assign",
  "enroll_send",
  "enroll_monitor",
  "enroll_extend",
  "enroll_verify",
  "data_contact",
  "data_medical",
  "data_financial",
  "report_export",
  "report_analytics",
  "admin_bulk",
  "admin_templates",
  "admin_settings",
  "admin_staff",
]);

export const RolePermissionMatrixSchema = z.record(
  StaffRoleSchema,
  z.record(PermissionIdSchema, z.boolean()),
);

export const AdmissionDatesSchema = z
  .object({
    openDate: z.string(),
    closeDate: z.string(),
    decisionDate: z.string(),
    status: EnrollmentPeriodStatus,
  })
  .passthrough();

export const ParentResourceTypeSchema = z.enum(["handbook", "calendar"]);

export const ParentResourceSchema = z
  .object({
    title: z.string(),
    type: ParentResourceTypeSchema,
    date: z.string(),
  })
  .passthrough();

export const ApprovedDomainSchema = z
  .object({
    domain: z.string(),
    addedAt: z.string(),
    addedBy: z.string(),
  })
  .passthrough();

export const StaffSignupCodeStatusSchema = z.enum(["active", "revoked"]);

export const StaffSignupCodeSchema = z
  .object({
    codeId: z.string(),
    label: z.string(),
    maskedValue: z.string(),
    codeHash: z.string(),
    salt: z.string(),
    status: StaffSignupCodeStatusSchema,
    createdAt: z.string(),
    createdBy: z.string(),
    rotatedAt: z.string().nullable().optional(),
    rotatedBy: z.string().nullable().optional(),
    revokedAt: z.string().nullable().optional(),
    revokedBy: z.string().nullable().optional(),
  })
  .passthrough();

export const PendingStaffRequestStatusSchema = z.enum([
  "pending",
  "approved",
  "rejected",
]);

export const PendingStaffRequestSchema = z
  .object({
    requestId: z.string(),
    email: z.string(),
    username: z.string(),
    userSub: z.string().nullable().default(null),
    userPoolId: z.string().nullable().default(null),
    schoolId: z.string().nullable().default(null),
    firstName: z.string().nullable().default(null),
    lastName: z.string().nullable().default(null),
    requestedRole: StaffRoleSchema.default("teacher"),
    status: PendingStaffRequestStatusSchema.default("pending"),
    source: z.string().nullable().default(null),
    requestedAt: z.string(),
    lastSeenAt: z.string(),
    decidedAt: z.string().nullable().default(null),
    decidedBy: z.string().nullable().default(null),
    decisionNote: z.string().nullable().default(null),
    staffUserId: z.string().nullable().default(null),
  })
  .passthrough();

export const StaffProvisioningSchema = z
  .object({
    approvedDomains: z.array(ApprovedDomainSchema).default([]),
    staffSignupCodes: z.array(StaffSignupCodeSchema).default([]),
    pendingStaffRequests: z.array(PendingStaffRequestSchema).default([]),
  })
  .passthrough();

export const AdmissionRequirementModeSchema = z.enum([
  "hidden",
  "optional",
  "required",
]);

export const AdmissionRequirementScopeSchema = z.enum([
  "per_student",
  "family",
]);

export const AdmissionRequiredDocumentSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    required: z.boolean().default(false),
    visible: z.boolean().optional(),
    requirementMode: AdmissionRequirementModeSchema.optional(),
    gradeScope: z.array(z.string()).default([]),
    description: z.string().default(""),
    scope: AdmissionRequirementScopeSchema.default("per_student"),
  })
  .passthrough();

export const AdmissionApplicationAgreementSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    text: z.string(),
    required: z.boolean().default(true),
    visible: z.boolean().optional(),
    requirementMode: AdmissionRequirementModeSchema.optional(),
  })
  .passthrough();

export const VaccinationPolicySchema = z
  .object({
    allowReligiousExemptions: z.boolean(),
    requireBothParentsAcknowledgment: z.boolean(),
    policyText: z.string(),
  })
  .passthrough();

export const AdmissionInterviewSchedulingSchema = z
  .object({
    familySelfSchedulingEnabled: z.boolean().default(false),
  })
  .passthrough()
  .default({ familySelfSchedulingEnabled: false });

export const AdmissionConfigSchema = z
  .object({
    requiredDocuments: z.array(AdmissionRequiredDocumentSchema),
    transcriptsRequired: z.boolean(),
    transcriptGrades: z.array(z.string()),
    applicationAgreements: z
      .array(AdmissionApplicationAgreementSchema)
      .default([]),
    vaccinationPolicy: VaccinationPolicySchema,
    interviewScheduling: AdmissionInterviewSchedulingSchema,
  })
  .passthrough();

export const FinancialAidSubmissionRequirementSchema = z.enum([
  "open_cycle",
  "registration_fee_paid",
]);

export const FinancialAidCycleSchema = z
  .object({
    schoolYear: z.string(),
    openDate: z.string(),
    onTimeDeadline: z.string(),
    hardCutoffDeadline: z.string(),
    lateFeeAmount: z.number(),
    submissionRequirement:
      FinancialAidSubmissionRequirementSchema.default("open_cycle"),
  })
  .passthrough();

export const FinancialAidBudgetSchema = z
  .object({
    annualBudget: z.number(),
    warningThreshold: z.number(),
    criticalThreshold: z.number(),
  })
  .passthrough();

export const FinancialAidDocumentSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    enabled: z.boolean(),
  })
  .passthrough();

export const OverdueReminderFrequencySchema = z.enum([
  "daily",
  "every_2_days",
  "weekly",
]);

export const FinancialAidNotificationsSchema = z
  .object({
    parent: z
      .object({
        applicationSubmitted: z.boolean(),
        statusChange: z.boolean(),
        moreInfoNeeded: z.boolean(),
        decisionReady: z.boolean(),
        appealReceived: z.boolean(),
      })
      .passthrough(),
    staff: z
      .object({
        newApplication: z.boolean(),
        resubmission: z.boolean(),
        appealSubmitted: z.boolean(),
        overdueReminder: z.boolean(),
      })
      .passthrough(),
    overdueReminderFrequency: OverdueReminderFrequencySchema,
  })
  .passthrough();

export const FinancialAidHistoricalImportStatusSchema = z.enum([
  "idle",
  "uploading",
  "processing",
  "completed",
  "failed",
]);

export const FinancialAidImportSchema = z
  .object({
    status: FinancialAidHistoricalImportStatusSchema.default("idle"),
    importId: z.string().nullable().default(null),
    templateVersion: z.string().nullable().default(null),
    lastFileName: z.string().nullable().default(null),
    lastS3Key: z.string().nullable().default(null),
    lastImportedAt: z.string().nullable().default(null),
    rowCount: z.number().default(0),
    processedRows: z.number().default(0),
    acceptedRows: z.number().default(0),
    rejectedRows: z.number().default(0),
    errorSummary: z.string().nullable().default(null),
  })
  .passthrough();

export const FinancialAidSettingsSchema = z
  .object({
    cycle: FinancialAidCycleSchema,
    cycles: z.record(z.string(), FinancialAidCycleSchema).default({}),
    budget: FinancialAidBudgetSchema,
    requiredDocuments: z.array(FinancialAidDocumentSchema),
    notifications: FinancialAidNotificationsSchema,
    historicalImport: FinancialAidImportSchema,
    requirementPolicy: FinancialAidRequirementPolicySchema.default(
      DEFAULT_FINANCIAL_AID_REQUIREMENT_PROFILE,
    ),
    certificationStatements: z.record(z.string(), z.string()).default({}),
  })
  .passthrough();

export const TuitionRateCategoryBandSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    gradeKeys: z.array(z.string()).default([]),
    gender: GradeGenderSchema.default("all"),
    amount: z.number(),
  })
  .passthrough();

export const TuitionRateCategoryFeeSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    entryType: z.string(),
    rateDescription: z.string(),
    amount: z.number(),
    amountType: z.enum(["per_child", "per_family"]),
    exemptGradeKeys: z.array(z.string()).default([]),
    dueDate: z.string().nullable().default(null),
    conditional: z.boolean().optional(),
  })
  .passthrough();

export const TuitionRateCategorySchema = z
  .object({
    id: z.string(),
    label: z.string(),
    description: z.string().nullable().default(null),
    flatRateMode: z.boolean().default(false),
    flatRateAmount: z.number().nullable().default(null),
    tuitionRates: z.array(TuitionRateCategoryBandSchema).default([]),
    feeSchedule: z.array(TuitionRateCategoryFeeSchema).default([]),
    multiChildDiscountEligible: z.boolean().default(false),
    financialAidEligible: z.boolean().default(false),
    staffDiscountEligible: z.boolean().default(false),
    chinuchDiscountEligible: z.boolean().default(false),
    fundraisingObligationEnabled: z.boolean().default(false),
    fundraisingAmountPerChild: z.number().default(0),
  })
  .passthrough();

export const TuitionPaymentPlanSchema = z
  .object({
    id: z.string(),
    label: z.string(),
    installmentCount: z.number().int().nonnegative(),
    startDate: z.string(),
    endDate: z.string(),
    enabled: z.boolean().default(true),
  })
  .passthrough();

export const ModuleGovernanceDisabledVisibilitySchema = z.enum([
  "hidden",
  "preview-blocked",
]);

export const ModuleGovernanceRouteGroupSchema = z
  .object({
    routeGroupId: z.string(),
    enabled: z.boolean(),
    disabledVisibility: ModuleGovernanceDisabledVisibilitySchema.optional(),
  })
  .passthrough();

export const ModuleGovernanceModuleSchema = z
  .object({
    moduleId: z.string(),
    enabled: z.boolean(),
    disabledVisibility: ModuleGovernanceDisabledVisibilitySchema.optional(),
    routeGroups: z.array(ModuleGovernanceRouteGroupSchema).default([]),
  })
  .passthrough();

export const DEFAULT_MODULE_GOVERNANCE_STATE: {
  launchVisibilityProfileId: string | null;
  schoolModules: Array<z.infer<typeof ModuleGovernanceModuleSchema>>;
  parentModules: Array<z.infer<typeof ModuleGovernanceModuleSchema>>;
} = {
  launchVisibilityProfileId: null,
  schoolModules: [],
  parentModules: [],
};

export const ModuleGovernanceStateSchema = z
  .object({
    launchVisibilityProfileId: z.string().nullable().optional(),
    schoolModules: z.array(ModuleGovernanceModuleSchema).default([]),
    parentModules: z.array(ModuleGovernanceModuleSchema).default([]),
  })
  .passthrough();

const legacyToken = (...parts: string[]): string => parts.join("");

const LEGACY_SUPER_ADMIN_ROLE_ALIAS = legacyToken("super", "_", "admin");
const LEGACY_SUPER_ADMINISTRATOR_ROLE_ALIAS = legacyToken(
  "super",
  "_",
  "administrator",
);
const CANONICAL_SUPER_ADMIN_ROLE = "super-administrator";
const LEGACY_STAFF_ROLE_ALIAS_MAP: Record<
  string,
  z.infer<typeof StaffRoleSchema>
> = {
  [LEGACY_SUPER_ADMIN_ROLE_ALIAS]: "super-administrator",
  [LEGACY_SUPER_ADMINISTRATOR_ROLE_ALIAS]: "super-administrator",
  "super-administrator": "super-administrator",
  owner: "super-administrator",
  administrator: "admin",
  admin: "admin",
  principal: "principal",
  teacher: "teacher",
  nurse: "nurse",
  accounts: "accounts",
  accountant: "accounts",
  staff: "teacher",
  editor: "teacher",
  viewer: "teacher",
};

type SettingsPayloadRecord = Record<string, unknown>;
type SettingsCanonicalizationResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

const isPlainObject = (value: unknown): value is SettingsPayloadRecord =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const normalizeToken = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const hasOwn = (value: SettingsPayloadRecord, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const cloneSettingsPayloadValue = <T>(value: T): T => {
  const globalWithStructuredClone = globalThis as typeof globalThis & {
    structuredClone?: <U>(input: U) => U;
  };
  const structuredCloneFn =
    typeof globalThis === "object" &&
    "structuredClone" in globalThis &&
    typeof globalWithStructuredClone.structuredClone === "function"
      ? globalWithStructuredClone.structuredClone
      : null;
  if (structuredCloneFn) {
    return structuredCloneFn(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

const resolveCanonicalPersistedStaffRole = (
  roleValue: unknown,
): z.infer<typeof StaffRoleSchema> => {
  const normalizedRole = normalizeToken(roleValue);
  const aliasedRole = LEGACY_STAFF_ROLE_ALIAS_MAP[normalizedRole];
  if (aliasedRole) {
    return aliasedRole;
  }
  return StaffRoleSchema.safeParse(normalizedRole).success
    ? (normalizedRole as z.infer<typeof StaffRoleSchema>)
    : "teacher";
};

const shouldRewritePersistedStaffRole = (roleValue: unknown): boolean => {
  const rawRole = String(roleValue ?? "").trim();
  if (!rawRole) {
    return false;
  }
  return rawRole !== resolveCanonicalPersistedStaffRole(roleValue);
};

const canonicalizePersistedStaffEntries = (staffValue: unknown): unknown => {
  if (!Array.isArray(staffValue)) {
    return staffValue;
  }

  return staffValue.map((entry) => {
    if (!isPlainObject(entry)) {
      return entry;
    }
    const nextRole = resolveCanonicalPersistedStaffRole(entry.role);
    if (nextRole === entry.role) {
      return entry;
    }
    return {
      ...entry,
      role: nextRole,
    };
  });
};

const findSuperAdminPermissionConflict = (
  canonicalPermissions: SettingsPayloadRecord,
  legacyPermissions: SettingsPayloadRecord,
): string | null => {
  for (const [permissionKey, legacyValue] of Object.entries(
    legacyPermissions,
  )) {
    if (!hasOwn(canonicalPermissions, permissionKey)) {
      continue;
    }
    if (canonicalPermissions[permissionKey] !== legacyValue) {
      return permissionKey;
    }
  }
  return null;
};

const normalizePersistedPermissions = (
  permissionsValue: unknown,
): SettingsCanonicalizationResult<unknown> => {
  if (!isPlainObject(permissionsValue)) {
    return { ok: true, value: permissionsValue };
  }

  if (!hasOwn(permissionsValue, LEGACY_SUPER_ADMIN_ROLE_ALIAS)) {
    return { ok: true, value: permissionsValue };
  }

  const legacyPermissionsValue =
    permissionsValue[LEGACY_SUPER_ADMIN_ROLE_ALIAS];
  if (!isPlainObject(legacyPermissionsValue)) {
    return {
      ok: false,
      error:
        `Persisted staff_settings permissions.${LEGACY_SUPER_ADMIN_ROLE_ALIAS} ` +
        "must be an object when present.",
    };
  }

  const nextPermissions = cloneSettingsPayloadValue(permissionsValue);
  const legacyPermissions = cloneSettingsPayloadValue(legacyPermissionsValue);
  if (hasOwn(nextPermissions, CANONICAL_SUPER_ADMIN_ROLE)) {
    const canonicalPermissionsValue =
      nextPermissions[CANONICAL_SUPER_ADMIN_ROLE];
    if (!isPlainObject(canonicalPermissionsValue)) {
      return {
        ok: false,
        error:
          "Persisted staff_settings permissions.super-administrator must be an object when present.",
      };
    }
    const canonicalPermissions = cloneSettingsPayloadValue(
      canonicalPermissionsValue,
    );
    const conflictKey = findSuperAdminPermissionConflict(
      canonicalPermissions,
      legacyPermissions,
    );
    if (conflictKey) {
      return {
        ok: false,
        error:
          `Persisted staff_settings permissions.${CANONICAL_SUPER_ADMIN_ROLE} ` +
          `conflicts with permissions.${LEGACY_SUPER_ADMIN_ROLE_ALIAS} ` +
          `for permission "${conflictKey}".`,
      };
    }
    nextPermissions[CANONICAL_SUPER_ADMIN_ROLE] = {
      ...legacyPermissions,
      ...canonicalPermissions,
    };
  } else {
    nextPermissions[CANONICAL_SUPER_ADMIN_ROLE] = legacyPermissions;
  }

  delete nextPermissions[LEGACY_SUPER_ADMIN_ROLE_ALIAS];
  return { ok: true, value: nextPermissions };
};

const canonicalizePersistedSettingsInputResult = (
  input: unknown,
): SettingsCanonicalizationResult<unknown> => {
  if (!isPlainObject(input)) {
    return { ok: true, value: input };
  }

  const next = cloneSettingsPayloadValue(input);
  next.staff = canonicalizePersistedStaffEntries(next.staff);
  const permissionsResult = normalizePersistedPermissions(next.permissions);
  if (!permissionsResult.ok) {
    return permissionsResult;
  }
  next.permissions = permissionsResult.value;
  return { ok: true, value: next };
};

const SettingsStateSchemaBase = z
  .object({
    organization: OrganizationProfileSchema,
    academicYear: AcademicYearSchema,
    benchmarks: HistoricalBenchmarkSchema,
    divisions: z.array(DivisionSchema),
    grades: z.array(GradeSchema),
    staff: z.array(StaffUserSchema),
    staffProvisioning: StaffProvisioningSchema.default({
      approvedDomains: [],
      staffSignupCodes: [],
      pendingStaffRequests: [],
    }),
    admissionDates: AdmissionDatesSchema,
    tuitionRates: z.array(TuitionRateSchema),
    fees: z.array(FeeSchema),
    tuitionRateCategories: z.array(TuitionRateCategorySchema).default([]),
    tuitionPaymentPlans: z.array(TuitionPaymentPlanSchema).default([]),
    defaultTuitionRateCategoryId: z.string().nullable().default(null),
    parentResources: z.array(ParentResourceSchema),
    permissions: RolePermissionMatrixSchema,
    admissionConfig: AdmissionConfigSchema,
    financialAid: FinancialAidSettingsSchema,
    governance: ModuleGovernanceStateSchema.default(() => ({
      launchVisibilityProfileId: null,
      schoolModules: [],
      parentModules: [],
    })),
  })
  .passthrough();

export const canonicalizePersistedSettingsRecord = (
  input: unknown,
): unknown => {
  const result = canonicalizePersistedSettingsInputResult(input);
  if (!result.ok) {
    throw new Error(result.error);
  }
  return result.value;
};

export const canonicalizePersistedSettingsPatch = (
  patch: unknown,
): Record<string, unknown> => {
  if (!isPlainObject(patch)) {
    return {};
  }
  return canonicalizePersistedSettingsRecord(patch) as Record<string, unknown>;
};

const persistedStaffUsesLegacyAlias = (staffValue: unknown): boolean =>
  Array.isArray(staffValue) &&
  staffValue.some(
    (entry) =>
      isPlainObject(entry) && shouldRewritePersistedStaffRole(entry.role),
  );

const persistedPermissionsUseLegacyAlias = (
  permissionsValue: unknown,
): boolean =>
  isPlainObject(permissionsValue) &&
  hasOwn(permissionsValue, LEGACY_SUPER_ADMIN_ROLE_ALIAS);

export const buildCanonicalPersistedSettingsRewritePatch = ({
  rawPayload,
  canonicalSettings,
}: {
  rawPayload: unknown;
  canonicalSettings?: {
    admissionConfig?: unknown;
    staff: unknown;
    permissions: unknown;
  };
}): Record<string, unknown> => {
  if (!isPlainObject(rawPayload)) {
    return {};
  }

  const canonical =
    canonicalSettings ??
    (SettingsStateSchema.parse(rawPayload) as unknown as {
      admissionConfig: unknown;
      staff: unknown;
      permissions: unknown;
    });
  const patch: Record<string, unknown> = {};

  const rawAdmissionConfig = isPlainObject(rawPayload.admissionConfig)
    ? rawPayload.admissionConfig
    : {};
  const rawInterviewScheduling = isPlainObject(
    rawAdmissionConfig.interviewScheduling,
  )
    ? rawAdmissionConfig.interviewScheduling
    : {};
  if (!hasOwn(rawInterviewScheduling, "familySelfSchedulingEnabled")) {
    patch.admissionConfig = cloneSettingsPayloadValue(
      canonical.admissionConfig,
    );
  }

  if (persistedStaffUsesLegacyAlias(rawPayload.staff)) {
    patch.staff = cloneSettingsPayloadValue(canonical.staff);
  }

  if (persistedPermissionsUseLegacyAlias(rawPayload.permissions)) {
    patch.permissions = {
      ...(cloneSettingsPayloadValue(
        canonical.permissions,
      ) as SettingsPayloadRecord),
      [LEGACY_SUPER_ADMIN_ROLE_ALIAS]: null,
    };
  }

  return patch;
};

export const SettingsStateSchema = z
  .unknown()
  .transform((input, ctx) => {
    const result = canonicalizePersistedSettingsInputResult(input);
    if (!result.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: result.error,
      });
      return z.NEVER;
    }
    return result.value;
  })
  .pipe(SettingsStateSchemaBase);

export type OrganizationProfile = z.infer<typeof OrganizationProfileSchema>;
export type AcademicYear = z.infer<typeof AcademicYearSchema>;
export type HistoricalBenchmark = z.infer<typeof HistoricalBenchmarkSchema>;
export type Division = z.infer<typeof DivisionSchema>;
export type Grade = z.infer<typeof GradeSchema>;
export type GradeGender = z.infer<typeof GradeGenderSchema>;
export type StaffUser = z.infer<typeof StaffUserSchema>;
export type StaffRole = z.infer<typeof StaffRoleSchema>;
export type StaffStatus = z.infer<typeof StaffStatusSchema>;
export type PermissionId = z.infer<typeof PermissionIdSchema>;
export type RolePermissionMatrix = z.infer<typeof RolePermissionMatrixSchema>;
export type AdmissionDates = z.infer<typeof AdmissionDatesSchema>;
export type TuitionRate = z.infer<typeof TuitionRateSchema>;
export type Fee = z.infer<typeof FeeSchema>;
export type TuitionRateCategoryBand = z.infer<
  typeof TuitionRateCategoryBandSchema
>;
export type TuitionRateCategoryFee = z.infer<
  typeof TuitionRateCategoryFeeSchema
>;
export type TuitionRateCategory = z.infer<typeof TuitionRateCategorySchema>;
export type TuitionPaymentPlan = z.infer<typeof TuitionPaymentPlanSchema>;
export type ParentResourceType = z.infer<typeof ParentResourceTypeSchema>;
export type ParentResource = z.infer<typeof ParentResourceSchema>;
export type ApprovedDomain = z.infer<typeof ApprovedDomainSchema>;
export type StaffSignupCodeStatus = z.infer<typeof StaffSignupCodeStatusSchema>;
export type StaffSignupCode = z.infer<typeof StaffSignupCodeSchema>;
export type PendingStaffRequestStatus = z.infer<
  typeof PendingStaffRequestStatusSchema
>;
export type PendingStaffRequest = z.infer<typeof PendingStaffRequestSchema>;
export type StaffProvisioning = z.infer<typeof StaffProvisioningSchema>;
export type AdmissionConfig = z.infer<typeof AdmissionConfigSchema>;
export type OverdueReminderFrequency = z.infer<
  typeof OverdueReminderFrequencySchema
>;
export type FinancialAidSubmissionRequirement = z.infer<
  typeof FinancialAidSubmissionRequirementSchema
>;
export type FinancialAidCycle = z.infer<typeof FinancialAidCycleSchema>;
export type FinancialAidSettings = z.infer<typeof FinancialAidSettingsSchema>;
export type FinancialAidCycleStatus = FinancialAidCycle & {
  isOpen: boolean;
  isClosed: boolean;
  isLateWindow: boolean;
};
export type ModuleGovernanceRouteGroup = z.infer<
  typeof ModuleGovernanceRouteGroupSchema
>;
export type ModuleGovernanceModule = z.infer<
  typeof ModuleGovernanceModuleSchema
>;
export type ModuleGovernanceDisabledVisibility = z.infer<
  typeof ModuleGovernanceDisabledVisibilitySchema
>;
export type ModuleGovernanceState = z.infer<typeof ModuleGovernanceStateSchema>;
export type SettingsState = z.infer<typeof SettingsStateSchema>;

const readFinancialAidSchoolYear = (value: unknown): string =>
  String(value ?? "").trim();

const DEFAULT_FINANCIAL_AID_TIME_ZONE = "America/New_York";
const DEFAULT_FINANCIAL_AID_CYCLE_DATES = {
  openDate: "01-01",
  onTimeDeadline: "05-15",
  hardCutoffDeadline: "06-15",
};

const resolveCalendarYear = (
  nowMs = Date.now(),
  timeZone = DEFAULT_FINANCIAL_AID_TIME_ZONE,
): number => {
  const date = new Date(nowMs);
  try {
    const formattedYear = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
    }).format(date);
    const year = Number(formattedYear);
    if (Number.isFinite(year) && year > 0) {
      return year;
    }
  } catch {
    // Fall back to UTC if the runtime lacks IANA time-zone data.
  }
  return date.getUTCFullYear();
};

export const resolveCurrentFinancialAidSchoolYear = (
  nowMs = Date.now(),
  timeZone = DEFAULT_FINANCIAL_AID_TIME_ZONE,
): string => {
  const startYear = resolveCalendarYear(nowMs, timeZone);
  return `${startYear}-${startYear + 1}`;
};

const cloneFinancialAidCycle = (cycle: FinancialAidCycle): FinancialAidCycle =>
  cloneSettingsPayloadValue(cycle);

const parseFinancialAidCycle = (
  value: unknown,
  schoolYearOverride = "",
): FinancialAidCycle | null => {
  if (!isPlainObject(value)) {
    return null;
  }
  const schoolYear =
    readFinancialAidSchoolYear(schoolYearOverride) ||
    readFinancialAidSchoolYear(value.schoolYear);
  const candidate = {
    ...value,
    schoolYear,
  };
  const parsed = FinancialAidCycleSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
};

const financialAidCycleDateMs = (value: string | null | undefined): number | null => {
  const next = String(value ?? "").trim();
  if (!next) return null;
  const parsed = Date.parse(`${next}T00:00:00Z`);
  return Number.isFinite(parsed) ? parsed : null;
};

export const listFinancialAidCycles = (
  financialAid: FinancialAidSettings,
): FinancialAidCycle[] => {
  const cyclesByYear = new Map<string, FinancialAidCycle>();

  const cyclesRecord = isPlainObject(
    (financialAid as Record<string, unknown>).cycles,
  )
    ? ((financialAid as Record<string, unknown>).cycles as Record<
        string,
        unknown
      >)
    : {};
  for (const [schoolYear, rawCycle] of Object.entries(cyclesRecord)) {
    const cycle = parseFinancialAidCycle(rawCycle, schoolYear);
    if (cycle && readFinancialAidSchoolYear(cycle.schoolYear)) {
      cyclesByYear.set(cycle.schoolYear, cycle);
    }
  }

  const primaryCycle = parseFinancialAidCycle(financialAid.cycle);
  if (
    primaryCycle &&
    readFinancialAidSchoolYear(primaryCycle.schoolYear) &&
    !cyclesByYear.has(primaryCycle.schoolYear)
  ) {
    cyclesByYear.set(primaryCycle.schoolYear, primaryCycle);
  }

  return [...cyclesByYear.values()].sort((left, right) =>
    left.schoolYear.localeCompare(right.schoolYear),
  );
};

export const getFinancialAidCycleForYear = (
  financialAid: FinancialAidSettings,
  schoolYear: unknown,
): FinancialAidCycle | null => {
  const requestedYear = readFinancialAidSchoolYear(schoolYear);
  if (!requestedYear) {
    return null;
  }
  const cycle = listFinancialAidCycles(financialAid).find(
    (candidate) => candidate.schoolYear === requestedYear,
  );
  return cycle ? cloneFinancialAidCycle(cycle) : null;
};

export const getCurrentFinancialAidCycle = (
  financialAid: FinancialAidSettings,
  nowMs = Date.now(),
): FinancialAidCycle | null =>
  getFinancialAidCycleForYear(
    financialAid,
    resolveCurrentFinancialAidSchoolYear(nowMs),
  );

const readFinancialAidSchoolYearStart = (
  schoolYear: unknown,
): number | null => {
  const match = String(schoolYear ?? "")
    .trim()
    .match(/^(\d{4})\s*-\s*\d{4}$/);
  const startYear = Number(match?.[1]);
  return Number.isFinite(startYear) && startYear > 0 ? startYear : null;
};

const alignFinancialAidCycleDateToSchoolYear = (
  value: unknown,
  schoolYear: string,
  fallbackMonthDay: string,
): string => {
  const startYear = readFinancialAidSchoolYearStart(schoolYear);
  const monthDay =
    String(value ?? "")
      .trim()
      .match(/^\d{4}-(\d{2}-\d{2})$/)?.[1] ?? fallbackMonthDay;
  return startYear ? `${startYear}-${monthDay}` : String(value ?? "").trim();
};

export const buildDefaultFinancialAidCycleForYear = (
  financialAid: FinancialAidSettings,
  schoolYear = resolveCurrentFinancialAidSchoolYear(),
): FinancialAidCycle => {
  const resolvedSchoolYear =
    readFinancialAidSchoolYear(schoolYear) ||
    resolveCurrentFinancialAidSchoolYear();
  const exactTemplate = getFinancialAidCycleForYear(
    financialAid,
    resolvedSchoolYear,
  );
  const template =
    exactTemplate ??
    parseFinancialAidCycle(financialAid.cycle) ??
    listFinancialAidCycles(financialAid)[0] ??
    null;
  const hasExactTemplate = Boolean(exactTemplate);

  return FinancialAidCycleSchema.parse({
    ...(template ?? {}),
    schoolYear: resolvedSchoolYear,
    openDate: alignFinancialAidCycleDateToSchoolYear(
      template?.openDate,
      resolvedSchoolYear,
      DEFAULT_FINANCIAL_AID_CYCLE_DATES.openDate,
    ),
    onTimeDeadline: alignFinancialAidCycleDateToSchoolYear(
      template?.onTimeDeadline,
      resolvedSchoolYear,
      DEFAULT_FINANCIAL_AID_CYCLE_DATES.onTimeDeadline,
    ),
    hardCutoffDeadline: alignFinancialAidCycleDateToSchoolYear(
      template?.hardCutoffDeadline,
      resolvedSchoolYear,
      DEFAULT_FINANCIAL_AID_CYCLE_DATES.hardCutoffDeadline,
    ),
    lateFeeAmount: hasExactTemplate ? Number(template?.lateFeeAmount ?? 0) : 0,
    submissionRequirement: hasExactTemplate
      ? (template?.submissionRequirement ?? "open_cycle")
      : "open_cycle",
  });
};

export const upsertFinancialAidCycle = (
  financialAid: FinancialAidSettings,
  cycle: FinancialAidCycle,
  options: { setPrimary?: boolean } = {},
): FinancialAidSettings => {
  const parsedCycle = FinancialAidCycleSchema.parse(cycle);
  const primaryCycle = parseFinancialAidCycle(financialAid.cycle);
  const cycles = Object.fromEntries(
    listFinancialAidCycles(financialAid).map((entry) => [
      entry.schoolYear,
      cloneFinancialAidCycle(entry),
    ]),
  );
  cycles[parsedCycle.schoolYear] = cloneFinancialAidCycle(parsedCycle);
  const shouldSetPrimary =
    options.setPrimary ??
    (!primaryCycle ||
      !readFinancialAidSchoolYear(primaryCycle.schoolYear) ||
      primaryCycle.schoolYear === parsedCycle.schoolYear);
  const nextPrimaryCycle =
    shouldSetPrimary || !primaryCycle ? parsedCycle : primaryCycle;

  return {
    ...financialAid,
    cycle: cloneFinancialAidCycle(nextPrimaryCycle),
    cycles,
  };
};

export const resolveFinancialAidCycleStatus = (
  cycle: FinancialAidCycle,
  nowMs = Date.now(),
): FinancialAidCycleStatus => {
  const openDateMs = financialAidCycleDateMs(cycle.openDate);
  const onTimeDeadlineMs = financialAidCycleDateMs(cycle.onTimeDeadline);
  const hardCutoffDeadlineMs = financialAidCycleDateMs(cycle.hardCutoffDeadline);
  const isClosed = hardCutoffDeadlineMs != null && nowMs >= hardCutoffDeadlineMs;
  const isOpen =
    openDateMs != null
    && nowMs >= openDateMs
    && (hardCutoffDeadlineMs == null || nowMs < hardCutoffDeadlineMs);
  const isLateWindow = onTimeDeadlineMs != null && nowMs >= onTimeDeadlineMs && !isClosed;
  return {
    ...cloneFinancialAidCycle(cycle),
    isOpen,
    isClosed,
    isLateWindow,
  };
};

export const resolveOpenFinancialAidCycle = (
  financialAid: FinancialAidSettings,
  nowMs = Date.now(),
): FinancialAidCycleStatus | null => {
  const currentCycle = getCurrentFinancialAidCycle(financialAid, nowMs);
  const currentStatus = currentCycle
    ? resolveFinancialAidCycleStatus(currentCycle, nowMs)
    : null;
  if (currentStatus && !currentStatus.isClosed) {
    return currentStatus;
  }

  const cycles = listFinancialAidCycles(financialAid);
  const openDateMs = (cycle: FinancialAidCycle): number | null =>
    financialAidCycleDateMs(cycle.openDate);
  const byLatestOpenDate = (
    left: FinancialAidCycle,
    right: FinancialAidCycle,
  ): number => {
    const leftOpenDate = openDateMs(left) ?? 0;
    const rightOpenDate = openDateMs(right) ?? 0;
    return (
      rightOpenDate - leftOpenDate ||
      right.schoolYear.localeCompare(left.schoolYear)
    );
  };
  const openCycles = cycles
    .filter((cycle) => {
      const openDate = openDateMs(cycle);
      if (openDate == null || openDate > nowMs) return false;
      const hardCutoff = financialAidCycleDateMs(cycle.hardCutoffDeadline);
      return hardCutoff == null || nowMs < hardCutoff;
    })
    .sort(byLatestOpenDate);

  if (openCycles[0]) {
    return resolveFinancialAidCycleStatus(openCycles[0], nowMs);
  }

  const upcomingCycles = cycles
    .filter((cycle) => {
      const openDate = openDateMs(cycle);
      return openDate != null && openDate > nowMs;
    })
    .sort((left, right) => {
      const leftOpenDate = openDateMs(left) ?? Number.MAX_SAFE_INTEGER;
      const rightOpenDate = openDateMs(right) ?? Number.MAX_SAFE_INTEGER;
      return (
        leftOpenDate - rightOpenDate ||
        left.schoolYear.localeCompare(right.schoolYear)
      );
    });
  if (upcomingCycles[0]) {
    return resolveFinancialAidCycleStatus(upcomingCycles[0], nowMs);
  }

  if (currentStatus) {
    return currentStatus;
  }

  const latestCycle = cycles.sort(byLatestOpenDate)[0];
  return latestCycle
    ? resolveFinancialAidCycleStatus(latestCycle, nowMs)
    : null;
};

export const SETTINGS_STATE_PATCH_KEYS = [
  "organization",
  "academicYear",
  "benchmarks",
  "divisions",
  "grades",
  "staff",
  "staffProvisioning",
  "admissionDates",
  "tuitionRates",
  "fees",
  "tuitionRateCategories",
  "tuitionPaymentPlans",
  "defaultTuitionRateCategoryId",
  "parentResources",
  "permissions",
  "admissionConfig",
  "financialAid",
  "governance",
] as const satisfies ReadonlyArray<keyof SettingsState>;
export const SettingsStatePatchKeySchema = z.enum(SETTINGS_STATE_PATCH_KEYS);
export type SettingsStatePatchKey = z.infer<typeof SettingsStatePatchKeySchema>;
export type IntegrationStatus = z.infer<typeof IntegrationStatusSchema>;
export type ModuleConfig = ModuleConfigType;
export * from "./financialAidRequirementPolicy";
export * from "./router-contract";
export * from "./settingsGovernance";

const DEFAULT_SUPER_ADMIN_PERMISSIONS = {
  app_view: true,
  app_review: true,
  app_status: true,
  app_notes: true,
  app_assign: true,
  enroll_send: true,
  enroll_monitor: true,
  enroll_extend: true,
  enroll_verify: true,
  data_contact: true,
  data_medical: true,
  data_financial: true,
  report_export: true,
  report_analytics: true,
  admin_bulk: true,
  admin_templates: true,
  admin_settings: true,
  admin_staff: true,
};

const DEFAULT_VIEW_ONLY_PERMISSIONS = {
  app_view: true,
  app_review: false,
  app_status: false,
  app_notes: false,
  app_assign: false,
  enroll_send: false,
  enroll_monitor: false,
  enroll_extend: false,
  enroll_verify: false,
  data_contact: false,
  data_medical: false,
  data_financial: false,
  report_export: false,
  report_analytics: false,
  admin_bulk: false,
  admin_templates: false,
  admin_settings: false,
  admin_staff: false,
};

export const DEFAULT_SETTINGS_STATE: SettingsState = {
  organization: {
    name: "Yeshiva Shaarei Tzion, New Jersey",
    shortName: "YSTNJ",
    primaryColor: "#253C3F",
    secondaryColor: "#0F172A",
    address: {
      street: "425 Cleveland Avenue",
      city: "Highland Park",
      state: "NJ",
      zip: "08904",
    },
    phone: "(732) 777-0029",
    phoneExt: "x211",
    emailAdmissions: "boysinfo@ystnj.org",
    emailSupport: "awallace@ystnj.org",
    website: "https://ystnj.org",
    missionStatement:
      "The mission of Yeshiva Shaarei Tzion is to inspire in each of our students a lifelong commitment to Torah and mitzvos.",
    timezone: "America/New_York",
    logo: null,
    ext: {
      source: "ystnj.org",
      divisionContacts: {
        boys: {
          address: "71 Ethel Road West, Piscataway Township, NJ 08854",
          phone: "(732) 777-0029",
          fax: "(732) 572-7001",
          email: "boysinfo@ystnj.org",
        },
        girls: {
          address: "425 Cleveland Avenue, Highland Park, NJ 08904",
          phone: "(732) 235-0042",
          fax: "(732) 235-0074",
          email: "girlsinfo@ystnj.org",
        },
        preschool: {
          address: "425 Cleveland Avenue, Highland Park, NJ 08904",
          phone: "(732) 819-7083",
          fax: "(732) 819-9561",
          email: "preschoolinfo@ystnj.org",
        },
        businessOffice: {
          address: "425 Cleveland Avenue, Highland Park, NJ 08904",
          phone: "(732) 777-0029",
          fax: "(732) 572-7001",
          extensions: {
            operationsManager: "x211",
            executiveDirector: "x222",
            directorOfOperations: "x223",
            comptroller: "x225",
            administrativeAssistant: "x227",
          },
        },
      },
      paletteSource: "app-shell",
      admissionsEmails: [
        "boysinfo@ystnj.org",
        "girlsinfo@ystnj.org",
        "preschoolinfo@ystnj.org",
      ],
      parnesHayomPricing: {
        oneClassAmount: 180,
        oneDivisionAmount: 500,
        schoolwideAmount: 1000,
      },
    },
  },
  academicYear: {
    label: "2025-2026",
    startDate: "2025-09-03",
    endDate: "2026-06-23",
  },
  benchmarks: {
    yearLabel: "2025-2026",
    applicants: 0,
    accepted: 0,
    enrolled: 0,
    siblingPercentage: 0,
    ext: {
      source: "unknown",
      note: "YSTNJ historical benchmarks are not published on ystnj.org.",
    },
  },
  divisions: [
    {
      id: "div-boys",
      type: "boys",
      displayName: "Boys Division",
      enabled: true,
      secretaryEmail: "boysinfo@ystnj.org",
    },
    {
      id: "div-girls",
      type: "girls",
      displayName: "Girls Division",
      enabled: true,
      secretaryEmail: "girlsinfo@ystnj.org",
    },
    {
      id: "div-preschool",
      type: "preschool",
      displayName: "Preschool Division",
      enabled: true,
      secretaryEmail: "preschoolinfo@ystnj.org",
    },
  ],
  grades: [
    {
      id: "boys-grade-1",
      label: "Grade 1",
      divisionId: "div-boys",
      gender: "boys",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "boys-grade-2",
      label: "Grade 2",
      divisionId: "div-boys",
      gender: "boys",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "boys-grade-3",
      label: "Grade 3",
      divisionId: "div-boys",
      gender: "boys",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "boys-grade-4",
      label: "Grade 4",
      divisionId: "div-boys",
      gender: "boys",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "boys-grade-5",
      label: "Grade 5",
      divisionId: "div-boys",
      gender: "boys",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "boys-grade-6",
      label: "Grade 6",
      divisionId: "div-boys",
      gender: "boys",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "boys-grade-7",
      label: "Grade 7",
      divisionId: "div-boys",
      gender: "boys",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "boys-grade-8",
      label: "Grade 8",
      divisionId: "div-boys",
      gender: "boys",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "girls-grade-1",
      label: "Grade 1",
      divisionId: "div-girls",
      gender: "girls",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "girls-grade-2",
      label: "Grade 2",
      divisionId: "div-girls",
      gender: "girls",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "girls-grade-3",
      label: "Grade 3",
      divisionId: "div-girls",
      gender: "girls",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "girls-grade-4",
      label: "Grade 4",
      divisionId: "div-girls",
      gender: "girls",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "girls-grade-5",
      label: "Grade 5",
      divisionId: "div-girls",
      gender: "girls",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "girls-grade-6",
      label: "Grade 6",
      divisionId: "div-girls",
      gender: "girls",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "girls-grade-7",
      label: "Grade 7",
      divisionId: "div-girls",
      gender: "girls",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "girls-grade-8",
      label: "Grade 8",
      divisionId: "div-girls",
      gender: "girls",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "preschool-nursery",
      label: "Full Day Nursery",
      divisionId: "div-preschool",
      gender: "all",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "preschool-pre1a",
      label: "Pre1-A",
      divisionId: "div-preschool",
      gender: "all",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
    {
      id: "preschool-kindergarten",
      label: "Kindergarten",
      divisionId: "div-preschool",
      gender: "all",
      capacity: 0,
      ext: {
        source: "unknown",
        note: "Grade capacities not published on ystnj.org.",
      },
    },
  ],
  staff: [
    {
      id: "staff-emalitzky",
      firstName: "Ezra",
      lastName: "Malitzky",
      email: "emalitzky@ystnj.org",
      role: "super-administrator",
      status: "active",
    },
    {
      id: "staff-ebak",
      firstName: "Esther Lyba",
      lastName: "Bak",
      email: "ebak@ystnj.org",
      role: "accounts",
      status: "active",
    },
    {
      id: "staff-awallace",
      firstName: "Avigal",
      lastName: "Wallace",
      email: "awallace@ystnj.org",
      role: "admin",
      status: "active",
    },
    {
      id: "staff-eneuberger",
      firstName: "Esti",
      lastName: "Neuberger",
      email: "eneuberger@ystnj.org",
      role: "admin",
      status: "active",
    },
    {
      id: "staff-ditzkowitz",
      firstName: "Duvid",
      lastName: "Itzkowitz",
      email: "ditzkowitz@ystnj.org",
      role: "admin",
      status: "active",
    },
  ],
  staffProvisioning: {
    approvedDomains: [],
    staffSignupCodes: [],
    pendingStaffRequests: [],
  },
  admissionDates: {
    openDate: "2025-01-01",
    closeDate: "2025-06-01",
    decisionDate: "2025-06-15",
    status: "open",
    ext: {
      source: "ystnj.org admissions + tuition deadlines",
      notes:
        "Admissions process begins in January; late registration surcharge after June 1; scholarship applications not accepted after June 15.",
    },
  },
  tuitionRates: [
    {
      id: "tuition-nursery",
      gradeLabel: "Full Day Nursery",
      gender: "all",
      amount: 9000,
    },
    {
      id: "tuition-kindergarten",
      gradeLabel: "Kindergarten",
      gender: "all",
      amount: 9500,
    },
    { id: "tuition-pre1a", gradeLabel: "Pre1-A", gender: "all", amount: 12300 },
    {
      id: "tuition-girls-1-3",
      gradeLabel: "Grades 1-3",
      gender: "girls",
      amount: 14400,
    },
    {
      id: "tuition-girls-4-6",
      gradeLabel: "Grades 4-6",
      gender: "girls",
      amount: 14650,
    },
    {
      id: "tuition-girls-7-8",
      gradeLabel: "Grades 7-8",
      gender: "girls",
      amount: 14900,
    },
    {
      id: "tuition-boys-1-3",
      gradeLabel: "Grades 1-3",
      gender: "boys",
      amount: 14900,
    },
    {
      id: "tuition-boys-4-6",
      gradeLabel: "Grades 4-6",
      gender: "boys",
      amount: 15400,
    },
    {
      id: "tuition-boys-7-8",
      gradeLabel: "Grades 7-8",
      gender: "boys",
      amount: 15900,
    },
  ],
  fees: [
    {
      id: "fee-registration",
      name: "Registration Fee (per child)",
      amount: 500,
    },
    { id: "fee-family-assessment", name: "Family Assessment", amount: 750 },
    {
      id: "fee-books-tech-supplies",
      name: "Books/Technology/Supplies Fee (per child)",
      amount: 500,
    },
    {
      id: "fee-facilities-management",
      name: "Facilities Management Fund (per family)",
      amount: 750,
    },
    { id: "fee-security", name: "Security Fee (per student)", amount: 100 },
    {
      id: "fee-late-registration",
      name: "Late Registration Surcharge (per child)",
      amount: 200,
    },
    {
      id: "fee-scholarship-late",
      name: "Scholarship Late Processing Fee",
      amount: 250,
    },
  ],
  tuitionRateCategories: [
    {
      id: "regular",
      label: "Regular",
      description: "YST Regular rate reference implementation.",
      flatRateMode: false,
      flatRateAmount: null,
      tuitionRates: [
        {
          id: "regular-nursery",
          label: "Nursery",
          gradeKeys: ["nursery"],
          gender: "all",
          amount: 9200,
        },
        {
          id: "regular-kindergarten",
          label: "Kindergarten",
          gradeKeys: ["kindergarten"],
          gender: "all",
          amount: 9700,
        },
        {
          id: "regular-pre1a",
          label: "Pre1A",
          gradeKeys: ["pre1a"],
          gender: "all",
          amount: 12600,
        },
        {
          id: "regular-girls-1-3",
          label: "Girls 1-3",
          gradeKeys: ["1", "2", "3"],
          gender: "girls",
          amount: 14800,
        },
        {
          id: "regular-girls-4-6",
          label: "Girls 4-6",
          gradeKeys: ["4", "5", "6"],
          gender: "girls",
          amount: 15100,
        },
        {
          id: "regular-girls-7-8",
          label: "Girls 7-8",
          gradeKeys: ["7", "8"],
          gender: "girls",
          amount: 15300,
        },
        {
          id: "regular-boys-1-3",
          label: "Boys 1-3",
          gradeKeys: ["1", "2", "3"],
          gender: "boys",
          amount: 15300,
        },
        {
          id: "regular-boys-4-6",
          label: "Boys 4-6",
          gradeKeys: ["4", "5", "6"],
          gender: "boys",
          amount: 15800,
        },
        {
          id: "regular-boys-7-8",
          label: "Boys 7-8",
          gradeKeys: ["7", "8"],
          gender: "boys",
          amount: 16300,
        },
      ],
      feeSchedule: [
        {
          id: "security_fee",
          name: "Security Fee",
          entryType: "security_fee",
          rateDescription: "$100 per child",
          amount: 100,
          amountType: "per_child",
          exemptGradeKeys: [],
          dueDate: null,
        },
        {
          id: "books_fee",
          name: "Books, Technology & Supplies",
          entryType: "books_fee",
          rateDescription: "$500 per student Pre1A-8",
          amount: 500,
          amountType: "per_child",
          exemptGradeKeys: ["nursery", "kindergarten"],
          dueDate: null,
        },
        {
          id: "family_assessment_fee",
          name: "Family Assessment",
          entryType: "family_assessment_fee",
          rateDescription: "$750 per family",
          amount: 750,
          amountType: "per_family",
          exemptGradeKeys: [],
          dueDate: null,
        },
        {
          id: "facilities_fee",
          name: "Facilities Management Fund",
          entryType: "facilities_fee",
          rateDescription: "$750 per family",
          amount: 750,
          amountType: "per_family",
          exemptGradeKeys: ["nursery", "kindergarten"],
          dueDate: null,
        },
        {
          id: "registration_fee",
          name: "Registration Fee",
          entryType: "registration_fee",
          rateDescription: "$350 per student",
          amount: 350,
          amountType: "per_child",
          exemptGradeKeys: [],
          dueDate: null,
        },
      ],
      multiChildDiscountEligible: true,
      financialAidEligible: true,
      staffDiscountEligible: true,
      chinuchDiscountEligible: true,
      fundraisingObligationEnabled: false,
      fundraisingAmountPerChild: 0,
    },
  ],
  tuitionPaymentPlans: [
    {
      id: "pay-in-full",
      label: "Pay in Full",
      installmentCount: 1,
      startDate: "2025-08-01",
      endDate: "2025-08-01",
      enabled: true,
    },
    {
      id: "10-month-plan",
      label: "10-Month Plan",
      installmentCount: 10,
      startDate: "2025-08-01",
      endDate: "2026-05-01",
      enabled: true,
    },
    {
      id: "12-month-plan",
      label: "12-Month Plan",
      installmentCount: 12,
      startDate: "2025-07-01",
      endDate: "2026-06-01",
      enabled: true,
    },
  ],
  defaultTuitionRateCategoryId: "regular",
  parentResources: [
    {
      title: "School Calendar 2025-2026",
      type: "calendar",
      date: "2025-09-03",
    },
  ],
  permissions: {
    "super-administrator": DEFAULT_SUPER_ADMIN_PERMISSIONS,
    admin: DEFAULT_SUPER_ADMIN_PERMISSIONS,
    principal: DEFAULT_VIEW_ONLY_PERMISSIONS,
    teacher: DEFAULT_VIEW_ONLY_PERMISSIONS,
    nurse: DEFAULT_VIEW_ONLY_PERMISSIONS,
    accounts: DEFAULT_VIEW_ONLY_PERMISSIONS,
  },
  admissionConfig: {
    requiredDocuments: [
      {
        id: "transcripts",
        label: "Previous Report Card",
        required: false,
        visible: false,
        requirementMode: "hidden",
        gradeScope: [
          "Grade 1",
          "Grade 2",
          "Grade 3",
          "Grade 4",
          "Grade 5",
          "Grade 6",
          "Grade 7",
          "Grade 8",
        ],
        description: "Required for Grade 1 through Grade 8.",
        scope: "per_student",
      },
      {
        id: "birth_certificate",
        label: "Birth Certificate",
        required: false,
        visible: false,
        requirementMode: "hidden",
        gradeScope: [],
        description: "Copy of original.",
        scope: "per_student",
      },
      {
        id: "immunization_records",
        label: "Immunization Records",
        required: false,
        visible: false,
        requirementMode: "hidden",
        gradeScope: [],
        description: "Up-to-date immunization records.",
        scope: "per_student",
      },
      {
        id: "occupational_history",
        label: "School History",
        required: false,
        visible: false,
        requirementMode: "hidden",
        gradeScope: [],
        description: "Current school, playgroup, or home-learning context.",
        scope: "per_student",
      },
      {
        id: "profile_photo",
        label: "Recent Photo",
        required: false,
        visible: false,
        requirementMode: "hidden",
        gradeScope: [],
        description: "Recent student photo.",
        scope: "per_student",
      },
      {
        id: "iep_504_plan",
        label: "IEP/504 Plan",
        required: false,
        visible: false,
        requirementMode: "hidden",
        gradeScope: [],
        description: "Upload if applicable to this child.",
        scope: "per_student",
      },
    ],
    transcriptsRequired: false,
    transcriptGrades: ["1", "2", "3", "4", "5", "6", "7", "8"],
    applicationAgreements: [
      {
        id: "tuition_policy",
        label: "Tuition Policy Agreement",
        text: "I/We understand that tuition and fee arrangements are completed during enrollment after an admissions decision.",
        required: false,
        visible: false,
        requirementMode: "hidden",
      },
      {
        id: "certification",
        label: "Certification",
        text: "I certify that all information provided in this application is true and accurate to the best of my knowledge.",
        required: true,
        visible: true,
        requirementMode: "required",
      },
    ],
    vaccinationPolicy: {
      allowReligiousExemptions: false,
      requireBothParentsAcknowledgment: false,
      policyText:
        "No child will be permitted into YST without full proof of immunization schedule.",
    },
    interviewScheduling: {
      familySelfSchedulingEnabled: false,
    },
  },
  financialAid: {
    cycle: {
      schoolYear: "2025-2026",
      openDate: "2025-01-01",
      onTimeDeadline: "2025-05-15",
      hardCutoffDeadline: "2025-06-15",
      lateFeeAmount: 250,
      submissionRequirement: "open_cycle",
      ext: {
        source: "ystnj.org tuition page",
        note: "Open date aligns with admissions process start in January.",
      },
    },
    cycles: {
      "2025-2026": {
        schoolYear: "2025-2026",
        openDate: "2025-01-01",
        onTimeDeadline: "2025-05-15",
        hardCutoffDeadline: "2025-06-15",
        lateFeeAmount: 250,
        submissionRequirement: "open_cycle",
        ext: {
          source: "ystnj.org tuition page",
          note: "Open date aligns with admissions process start in January.",
        },
      },
    },
    budget: {
      annualBudget: 0,
      warningThreshold: 80,
      criticalThreshold: 95,
      ext: {
        source: "unknown",
        note: "Annual budget not published on ystnj.org.",
      },
    },
    requiredDocuments: [],
    notifications: {
      parent: {
        applicationSubmitted: true,
        statusChange: true,
        moreInfoNeeded: true,
        decisionReady: true,
        appealReceived: true,
      },
      staff: {
        newApplication: true,
        resubmission: true,
        appealSubmitted: true,
        overdueReminder: true,
      },
      overdueReminderFrequency: "every_2_days",
    },
    historicalImport: {
      status: "idle",
      importId: null,
      templateVersion: null,
      lastFileName: null,
      lastS3Key: null,
      lastImportedAt: null,
      rowCount: 0,
      processedRows: 0,
      acceptedRows: 0,
      rejectedRows: 0,
      errorSummary: null,
    },
    requirementPolicy: DEFAULT_FINANCIAL_AID_REQUIREMENT_PROFILE,
    certificationStatements: {},
  },
  governance: {
    schoolModules: [],
    parentModules: [],
  },
};
